import loading from 'assets/pictures/loading.gif'
import { push } from '@lagunovsky/redux-react-router'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { orderBy, keys, groupBy, take } from 'lodash'
import PropTypes from 'prop-types'
import moment from 'moment'
import { getStationType } from '../../utils/StationUtils'
import { getFullDate, getTimestamp } from '../../utils/DateUtil'
import { getLabel } from '../../utils/StoreUtils'
import { getCampaignIcon, getCampaignNameCell, getCampaignProgress, getCampaignsWithBookmarks, TableProgressBar } from '../../campaign/utils/CampaignUtils'
import useCampaignBookmarks from '../../utils/customHook/useCampaignBookmarks'
import { getColorCampaign } from '../../utils/CampaignUtil'
import CampaignPortletPanel from '../../campaign/components/CampaignPortletPanel'
import CampaignAction from '../../campaign/actions/CampaignAction'
import useAbortController from '../../utils/customHook/useAbortController'
import { hasValue } from '../../utils/NumberUtil'
import MonitoredMaterielPortletPanel from '../../materiel/components/dashboard/MonitoredMaterielPortletPanel'
import { getUserBookmarksByStationType } from '../../utils/UserUtil'
import { componentHasHabilitations } from '../../utils/HabilitationUtil'
import {
    H_HYDRO_MODULE,
    H_MAT_MODULE,
} from '../../account/constants/AccessRulesConstants'
import HabilitationRequired from '../../utils/components/HabilitationRequired'
import { STATION_TYPE_CONSTANT, STATION_TYPE_NAME } from 'station/constants/StationConstants'
import UserThunk from 'administration/components/user/actions/UserThunk'
import useStateProgress from 'utils/customHook/useStateProgress'
import HydrometryThunk from 'hydrometry/actions/HydrometryThunk'
import { HydrometryActionConstant } from 'hydrometry/reducers/HydrometryReducer'
import StationAction from '../../station/actions/StationAction'
import useAccountSetting from '../../utils/customHook/useAccountSetting'
import MapSituationPie from '../../station/components/mapSituation/MapSituationPie'
import { Card, CardContent, CircularProgress, Grid, Icon, IconButton, Stack } from '@mui/material'
import { ALIGN, NewTable } from '../../components/datatable/NewTable'
import DtoMapSituationStat from '../../station/components/mapSituation/dto/DtoMapSituationStat'
import ProgressBar from '../../components/progress/ProgressBar'
import MultilineChartIcon from '@mui/icons-material/MultilineChart'
import { CardTitle } from '../../components/card/NewCard'
import { darkBlue } from '../../utils/constants/ColorTheme'

const hydroStationType = 4

const HydrometryMapSituationPieWrapper = ({ mapId, mapSituations }) => {
    const [dtoStats, setDtoStats] = useState([])
    const [isPieLoading, setIsPieLoading] = useState(true)

    useEffect(() => {
        if (!mapSituations || mapSituations.length === 0) {
            setIsPieLoading(false)
            return
        }
        const mapSituation = mapSituations.find(m => m.id === mapId)
        if (mapSituation) {
            const selectedDate = mapSituation.endDate || moment().valueOf()
            StationAction.promiseMapSituationResults('hydrometry', mapSituation, selectedDate).then(results => {
                const group = groupBy(results, 'trendColor')
                const stats = keys(group).map(key => {
                    const count = group[key].length
                    const trendColor = group[key][0].trendColor
                    return new DtoMapSituationStat({
                        mapId: mapSituation.id,
                        mapName: mapSituation.name,
                        date: selectedDate,
                        nb: count,
                        threshold: group[key][0].thresholdName || key,
                        color: (key === 'undefined' || key === 'missingData' || trendColor === 'white') ? 'lightgray' : trendColor,
                    })
                }).filter(item => item !== null)
                setDtoStats(stats)
                setIsPieLoading(false)
            })
        } else {
            setIsPieLoading(false)
        }
    }, [mapId, mapSituations])

    if (isPieLoading || dtoStats.length === 0) {
        return i18n.loading
    }

    return <MapSituationPie stats={dtoStats} st={STATION_TYPE_NAME.hydrometry} />
}

HydrometryMapSituationPieWrapper.propTypes = {
    mapId: PropTypes.number.isRequired,
    mapSituations: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool])),
}

const HydrometryCard = () => {
    const {
        userBookmarks,
        hydrometricStations,
        hydroStats,
        hydrometryCampaigns,
        hydrometryCampaignsProgress,
        monitoredMateriels,
        citiesIndex,
        contacts,
        userBookmarksStatus,
        hydrometricStationsStatus,
        hydroBookmarksStatsStatus,
        monitoredStations,
    } = useSelector(store => ({
        userBookmarks: store.UserReducer.userBookmarks,
        hydrometricStations: store.HydrometryReducer.hydrometricStations,
        hydroStats: store.HydrometryReducer.hydroBookmarksStats,
        hydrometryCampaigns: store.HydrometryReducer.hydrometryCampaigns,
        hydrometryCampaignsProgress: store.HydrometryReducer.hydrometryCampaignsProgress,
        monitoredMateriels: store.MaterielReducer.monitoredMateriels,
        citiesIndex: store.CityReducer.citiesIndex,
        contacts: store.ContactReducer.contacts,
        userBookmarksStatus: store.DataManagerReducer.user.userBookmarksStatus,
        hydrometricStationsStatus: store.DataManagerReducer.hydrometry.hydrometricStationsStatus,
        hydroBookmarksStatsStatus: store.DataManagerReducer.hydrometry.hydroBookmarksStatsStatus,
        monitoredStations: store.EventsReducer.monitoredStations,
    }), shallowEqual)

    const { progress: progressBar, isLoaded: dataLoaded } = useStateProgress([userBookmarksStatus, hydrometricStationsStatus, hydroBookmarksStatsStatus])

    const dispatch = useDispatch()

    const mapSituationWidgetObj = useAccountSetting('MAP_SITUATION_DASHBOARD_WIDGET', setting => JSON.parse(setting ?? '{}'))
    const [mapSituations, setMapSituations] = useState([])

    const bookmarks = useMemo(() => getUserBookmarksByStationType(userBookmarks, STATION_TYPE_NAME.hydrometry), [userBookmarks])

    const stationsForTable = useMemo(() => {
        const bookmarksStations = bookmarks.map(code => hydrometricStations.find(s => s.code === code)).filter(p => !!p).map(s => ({ ...s, isBookmark: true }))
        const monitored = monitoredStations.filter(s => s.type === STATION_TYPE_CONSTANT.hydrometry && hydrometricStations.some(s2 => s.id === s2.id)).map(s => ({ ...s, isMonitored: true }))
        const reduced = [...bookmarksStations, ...monitored].reduce((acc, s) => {
            const found = acc.find(s2 => s.id === s2.id)
            if (found) {
                return [...acc.filter(s2 => s2.id !== s.id), { ...s, ...found }]
            }
            return [...acc, s]
        }, [])
        return orderBy(reduced, [s => s.isMonitored && s.isBookmark ? 2 : (s.isMonitored ? 1 : 0), 'name'], ['desc', 'asc'])
    }, [hydrometricStations, bookmarks, monitoredStations])

    const monitoredMaterielsFiltered = useMemo(() => monitoredMateriels.filter(m => m.typeSite === hydroStationType), [monitoredMateriels])

    useEffect(() => {
        dispatch(UserThunk.fetchBookmarks())
        dispatch(HydrometryThunk.fetchHydrometricStations())
        dispatch(CampaignAction.fetchHydrometryCampaigns())
    }, [dispatch])

    useEffect(() => {
        StationAction.promiseMapSituations('hydrometry').then(ms => setMapSituations(ms))
    }, [])

    useEffect(() => {
        const ids = stationsForTable.map(b => b.id)
        dispatch(HydrometryThunk.fetchHydroBookmarksStats(ids))
    }, [bookmarks, dispatch, stationsForTable, hydrometricStations?.length])

    useEffect(() => () => dispatch(HydrometryActionConstant.resetHydroStats()), [dispatch])

    const { bookmarkIds: campaignBookmarkIds } = useCampaignBookmarks(STATION_TYPE_NAME.hydrometry)

    const orderedCampaigns = useMemo(() => {
        const currentCampaigns = orderBy(hydrometryCampaigns.filter(c => c.statut === 2), c => getTimestamp(c.beginningApplication), 'desc')
        return getCampaignsWithBookmarks(hydrometryCampaigns, currentCampaigns, campaignBookmarkIds)
    }, [hydrometryCampaigns, campaignBookmarkIds])

    const { controllerRef, initController } = useAbortController()

    useEffect(() => {
        initController()
        if (orderedCampaigns.length) {
            dispatch(CampaignAction.getCampaignProgressSequentially(STATION_TYPE_NAME.hydrometry, take(orderedCampaigns, 3), controllerRef.current.signal))
        }
    }, [orderedCampaigns, controllerRef])

    const campaignsData = useMemo(() => orderedCampaigns.map(campaign => {
        const {
            nbStation = 0,
            nbStationValidated = 0,
            id,
        } = hydrometryCampaignsProgress.find(cp => cp.id === campaign.id) || {}
        const progress = getCampaignProgress(nbStationValidated, nbStation)
        return {
            id: campaign.id,
            qualificationTableColor: {
                color: getColorCampaign(false, progress, 2),
            },
            nullValue: getCampaignIcon(campaign.campaignType, 'small'),
            name: getCampaignNameCell(campaign.name, campaignBookmarkIds.includes(campaign.id)),
            nbStations: nbStation,
            startDate: campaign.beginningApplication,
            endDate: campaign.endingApplication,
            progression: hasValue(id) ? <TableProgressBar nb={nbStationValidated} nbTotal={nbStation} progress={progress} /> : <CircularProgress size={25} />,
            referent: getLabel(contacts, campaign.contactCode, 'mnemonique'),
        }
    }), [orderedCampaigns, hydrometryCampaignsProgress, contacts, campaignBookmarkIds])

    const headerIcon = (
        <IconButton
            sx={{ color: '#000', fontSize: '1.8rem', padding: '1px' }}
            onClick={() => dispatch(push('/hydrometry/campaign'))}
        >
            <Icon fontSize='inherit'>nature_people</Icon>
        </IconButton>
    )

    const campaignsPanel = useMemo(() => {
        if (campaignsData.length > 0) {
            return (
                <CampaignPortletPanel
                    campaigns={campaignsData}
                    stationType={STATION_TYPE_NAME.hydrometry}
                    headers={[
                        { key: 'qualificationTableColor', value: headerIcon, width: 26, sticky: ALIGN.LEFT },
                        { key: 'campaignIcon', value: '' }, { key: 'name', value: i18n.campaign },
                        'nbStations', 'startDate', 'endDate', 'progression',
                    ]}
                />
            )
        }
        return null
    }, [campaignsData])

    const hydroMapIds = useMemo(() => {
        return keys(mapSituationWidgetObj).filter(key => key.startsWith('hydrometry') && !!mapSituationWidgetObj[key]).map(k => parseInt(k.replace('hydrometry-', '')))
    }, [mapSituationWidgetObj])

    const mapSituationCard = useMemo(() => {
        if (!hydroMapIds.length) return null
        return (
            <Grid container justifyContent='center' alignItems='center' spacing={3} sx={{ paddingBottom: '10px' }}>
                {hydroMapIds.map((mapId, i) => (
                    <Grid key={mapId} size={4}>
                        <HydrometryMapSituationPieWrapper key={i} mapId={mapId} mapSituations={mapSituations} />
                    </Grid>
                ))}
            </Grid>
        )
    }, [hydroMapIds, mapSituations])

    const bookmarksStationPanel = useMemo(() => {
        if (!stationsForTable.length && !hydroMapIds.length) {
            return null
        }
        const stationResult = stationsForTable.map(station => {
            const cityFound = citiesIndex[station.townCode]
            const stats = hydroStats.find(s => s.code === station.code)
            return {
                id: station.id,
                code: station.code,
                name: station.name,
                altitude: station.altitude,
                stationType: station.stationType ? getStationType(parseInt(station.stationType)).libelle : '',
                city: cityFound ? `${cityFound.name} [${cityFound.code}]` : station.townCode,
                lastValueHeight: {
                    color: 'white',
                    value: stats?.lastValueHeight ?? '',
                    tooltip: getFullDate(stats?.lastDateHeight).toString() ?? '',
                },
                lastValueFlow: {
                    color: 'white',
                    value: stats?.lastValueFlow ?? '',
                    tooltip: getFullDate(stats?.lastDateFlow).toString() ?? '',
                },
                lastObservationLabel: stats?.lastObservationLabel ?? '',
                nullValue: {
                    value: '',
                    align: 'center',
                    leftIcon: station.isMonitored && station.isBookmark ? {
                        icon: 'notificationsNone',
                        color: 'red',
                        tooltip: i18n.oversight,
                    } : undefined,
                    rightIcon: {
                        icon: station.isBookmark ? 'star' : 'notificationsNone',
                        color: station.isBookmark ? 'orange' : 'red',
                        tooltip: station.isBookmark ? i18n.bookmark : i18n.oversight,
                    },
                },
                comment: station.eventComment,
            }
        })
        return (
            <NewTable
                headers={['nullValue', 'code', 'city', 'name', 'stationType', 'lastValueHeight', 'lastValueFlow', 'lastObservationLabel', 'comment']}
                rows={stationResult}
                onClickRow={s => dispatch(push(`/station/hydrometry/${s.id}`))}
            />
        )
    }, [citiesIndex, stationsForTable, hydroStats, dispatch, hydroMapIds])

    if (dataLoaded) {
        if (!(componentHasHabilitations(H_HYDRO_MODULE) && (stationsForTable.length > 0 || hydroMapIds.length > 0 || campaignsData.length > 0 || monitoredMaterielsFiltered.length > 0))) {
            return null
        }
        return (
            <Card>
                <CardTitle
                    title={i18n[STATION_TYPE_NAME.hydrometry]}
                    icon={<MultilineChartIcon fontSize='large' />}
                    color={darkBlue}
                    onClick={() => dispatch(push(`/${STATION_TYPE_NAME.hydrometry}`))}
                />
                {mapSituationCard}
                <Stack spacing={1}>
                    {bookmarksStationPanel}
                    {campaignsPanel}
                    {monitoredMaterielsFiltered.length > 0 && (
                        <HabilitationRequired habilitation={H_MAT_MODULE}>
                            <MonitoredMaterielPortletPanel noHeader stationType={hydroStationType} />
                        </HabilitationRequired>
                    )}
                </Stack>
            </Card>
        )
    }
    return (
        <Card>
            <CardContent>
                <Grid container justifyContent='center' alignItems='center' direction='column' sx={{ pt: 2.5 }}>
                    <img src={loading} />
                    <Grid size={3}>
                        <ProgressBar
                            withMessage
                            message={i18n.hydrometryCardInProgress}
                            progress={progressBar}
                        />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

export default HydrometryCard